import { create } from 'zustand';
import { staffService, StaffMember } from '../services/staffService';

interface StaffStore {
  staffMembers: StaffMember[];
  isLoading: boolean;
  fetchStaffMembers: () => Promise<void>;
  addStaffMember: (member: Omit<StaffMember, 'id' | 'currentLeads' | 'createdAt'>) => Promise<void>;
  updateStaffMember: (id: string, updates: Partial<StaffMember>) => Promise<void>;
  deleteStaffMember: (id: string) => Promise<void>;
  getStaffMemberById: (id: string) => StaffMember | undefined;
  getActiveStaffMembers: () => StaffMember[];
}

export const useStaffStore = create<StaffStore>((set, get) => ({
  staffMembers: [],
  isLoading: false,

  fetchStaffMembers: async () => {
    set({ isLoading: true });
    try {
      const staffMembers = await staffService.getStaffMembers();
      set({ staffMembers, isLoading: false });
    } catch (error) {
      console.error('Failed to fetch staff members:', error);
      set({ isLoading: false });
    }
  },

  addStaffMember: async (member) => {
    const newMember = await staffService.createStaffMember(member);
    if (newMember) {
      set((state) => ({
        staffMembers: [...state.staffMembers, newMember],
      }));
    }
  },

  updateStaffMember: async (id, updates) => {
    const updatedMember = await staffService.updateStaffMember(id, updates);
    if (updatedMember) {
      set((state) => ({
        staffMembers: state.staffMembers.map((member) =>
          member.id === id ? updatedMember : member
        ),
      }));
    }
  },

  deleteStaffMember: async (id) => {
    const success = await staffService.deleteStaffMember(id);
    if (success) {
      set((state) => ({
        staffMembers: state.staffMembers.filter((member) => member.id !== id),
      }));
    }
  },

  getStaffMemberById: (id) => {
    return get().staffMembers.find((member) => member.id === id);
  },

  getActiveStaffMembers: () => {
    return get().staffMembers.filter((member) => member.isActive);
  },
}));

export type { StaffMember };